import {Box, useMergeRefs} from '@chakra-ui/react'
import loadable from '@loadable/component'
import React, {forwardRef, useEffect, useState} from 'react'
import useIntersectionObserver from '../hooks/use-intersection-observer'
import PropTypes from 'prop-types'

const ReferencedFallback = forwardRef(({fallback, ...props}, ref) => (
    <Box ref={ref}>{fallback ? React.cloneElement(fallback, props) : null}</Box>
))
if (process.env.NODE_ENV !== 'production') {
    ReferencedFallback.displayName = 'ReferencedFallback'
}

ReferencedFallback.propTypes = {
    fallback: PropTypes.node.isRequired
}

const IDLE_TIMEOUT_SECONDS = 3

function callOnIdle(cb) {
    if ('requestIdleCallback' in window) {
        return requestIdleCallback(cb, {timeout: IDLE_TIMEOUT_SECONDS * 1000})
    } else {
        return setTimeout(cb, IDLE_TIMEOUT_SECONDS * 1000)
    }
}

function cancelIdleCb(id) {
    if ('requestIdleCallback' in window) {
        return cancelIdleCallback(id)
    } else {
        return clearTimeout(id)
    }
}

/**
 * Lazy load a component after a delay or when it becomes visible.
 * @param {function(): Promise} importFunc
 * @param {object} [opts]
 * @param {boolan} [opts.loadOnIdle=false]
 * @param {any} [opts.fallback]
 */
const lazyLoadable = (importFunc, {loadOnIdle = false, fallback = null} = {}) => {
    const LoadableComponent = loadable(importFunc, {fallback, ssr: false})

    const LazyLoadable = forwardRef((props, forwardedRef) => {
        const [triggered, setTriggered] = useState(false)

        useEffect(() => {
            if (loadOnIdle) {
                const idleId = callOnIdle(() => setTriggered(true))
                return () => cancelIdleCb(idleId)
            }
        }, [])

        const [setFallbackElement, visible] = useIntersectionObserver({useOnce: true})
        // trigger loading of the component when it becomes visible
        if (visible && !triggered) {
            setTriggered(true)
        }

        const combinedRef = useMergeRefs(setFallbackElement, forwardedRef)

        const referencedFallback = <ReferencedFallback ref={combinedRef} fallback={fallback} />

        // if the component is visible or has been triggered, render it
        if (visible || triggered) {
            return <LoadableComponent {...props} ref={forwardedRef} fallback={referencedFallback} />
        }

        // otherwise, render the fallback
        return referencedFallback
    })

    if (process.env.NODE_ENV !== 'production') {
        const name = importFunc.name || 'Unknown'
        LazyLoadable.displayName = `LazyLoadable(${name})`
    }

    return LazyLoadable
}

export default lazyLoadable
